'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CookieConsent, DEFAULT_CONSENT, COOKIE_STORAGE_KEY, COOKIE_BANNER_SHOWN_KEY } from '@/lib/types/cookies';

interface CookieConsentContextType {
  consent: CookieConsent;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  hasConsent: (category: keyof Omit<CookieConsent, 'timestamp' | 'version'>) => boolean;
  showBanner: boolean;
  acceptAllCookies: () => void;
  rejectNonEssentialCookies: () => void;
  resetConsent: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  showSettings: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_CONSENT);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const savedConsent = localStorage.getItem(COOKIE_STORAGE_KEY);
    const bannerShown = localStorage.getItem(COOKIE_BANNER_SHOWN_KEY);

    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent) as CookieConsent;
        setConsent(parsedConsent);
      } catch (error) {
        console.error('Failed to parse saved consent:', error);
        // Reset to default if parsing fails
        localStorage.removeItem(COOKIE_STORAGE_KEY);
      }
    }

    // Show banner if user hasn't seen it and hasn't given consent
    if (!bannerShown && !savedConsent) {
      setShowBanner(true);
    }

    setIsInitialized(true);
  }, []);

  // Save consent to localStorage whenever it changes
  const saveConsent = (newConsent: CookieConsent) => {
    if (typeof window === 'undefined') return;

    const consentWithTimestamp = {
      ...newConsent,
      timestamp: Date.now(),
      version: '1.0'
    };

    // If functional consent is being withdrawn, clean up functional localStorage
    if (!newConsent.functional && consent.functional) {
      // Remove functional data when consent is withdrawn
      localStorage.removeItem('firstDayOfWeek');
      localStorage.removeItem('calendar-view-preference');
      console.log('Functional consent withdrawn - cleared functional localStorage data');
    }

    localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(consentWithTimestamp));
    localStorage.setItem(COOKIE_BANNER_SHOWN_KEY, 'true');
    setConsent(consentWithTimestamp);
  };

  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    const updatedConsent = { ...consent, ...newConsent };
    saveConsent(updatedConsent);
    setShowBanner(false);
  };

  const hasConsent = (category: keyof Omit<CookieConsent, 'timestamp' | 'version'>) => {
    return consent[category] === true;
  };

  const acceptAllCookies = () => {
    const allAcceptedConsent: CookieConsent = {
      essential: true,
      analytics: true,
      errorMonitoring: true,
      functional: true,
      timestamp: Date.now(),
      version: '1.0'
    };
    saveConsent(allAcceptedConsent);
    setShowBanner(false);
  };

  const rejectNonEssentialCookies = () => {
    const essentialOnlyConsent: CookieConsent = {
      essential: true,
      analytics: false,
      errorMonitoring: false,
      functional: false,
      timestamp: Date.now(),
      version: '1.0'
    };
    saveConsent(essentialOnlyConsent);
    setShowBanner(false);
  };

  const resetConsent = () => {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(COOKIE_STORAGE_KEY);
    localStorage.removeItem(COOKIE_BANNER_SHOWN_KEY);
    setConsent(DEFAULT_CONSENT);
    setShowBanner(true);
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  // Don't render children until initialized to prevent hydration issues
  if (!isInitialized) {
    return null;
  }

  const value: CookieConsentContextType = {
    consent,
    updateConsent,
    hasConsent,
    showBanner,
    acceptAllCookies,
    rejectNonEssentialCookies,
    resetConsent,
    openSettings,
    closeSettings,
    showSettings
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}